import type {
  GHACugLeakTierLine,
  GHADiscountSummaryLine,
  GHAExclusionRuleLine,
  GHALeakWindow,
} from '~/services/ConnectionGoogleService';
import type { GHAPropertyClassificationTier } from '~/services/ConnectionGoogleService';

import { CLASSIFICATION_TIER_OPTIONS, TIER_LABELS, TIER_ORDER } from './constants';

const tierLabel = (tier: string): string => TIER_LABELS[tier] ?? tier;

const tierSortIndex = (tier: string): number => {
  const index = TIER_ORDER.indexOf(tier);
  return index === -1 ? TIER_ORDER.length : index;
};

/** "3% Margin Retention" / "6% Percentage" — full words per GHA-442 review. */
export function formatDiscountKind(line: GHADiscountSummaryLine): string {
  const kind = line.discountType === 'margin_retention' ? 'Margin Retention' : 'Percentage';
  return `${line.discountValue}% ${kind}`;
}

/** Universal rules (country null) apply to every enabled country. */
export function formatCountry(country: string | null): string {
  return country ?? 'All countries';
}

/** "Expedia: do not discount Tier 1, Tier 2, Tier 3" with optional country/chain narrowing. */
export function formatExclusionRule(line: GHAExclusionRuleLine): string {
  const who = line.supplierName ?? line.supplier ?? 'All suppliers';

  const scopes: string[] = [];
  if (line.cugTiers != null && line.cugTiers.length > 0) {
    scopes.push(line.cugTiers.map(tierLabel).join(', '));
  } else {
    scopes.push('all tiers');
  }
  if (line.countries != null && line.countries.length > 0) {
    scopes.push(`in ${line.countries.join(', ')}`);
  }
  if (line.chainNames != null && line.chainNames.length > 0) {
    const chains = line.chainNames.map((chain) => chain ?? 'un-chained properties');
    scopes.push(`chains: ${chains.join(', ')}`);
  }

  return `${who}: do not discount ${scopes.join(' — ')}`;
}

/** "min ABW 0 · min LOS 1" per distinct coordinate; the estate currently has exactly one. */
export function formatRuleCoordinates(
  coordinates: Array<{ minAdvanceBookingWindow: number; minLengthOfStay: number }>,
): string {
  return coordinates
    .map((coordinate) => `min ABW ${coordinate.minAdvanceBookingWindow} · min LOS ${coordinate.minLengthOfStay}`)
    .join('; ');
}

/** Ordered [tier, label, count] entries for chip rendering, canonical tier order, zeros skipped. */
export function tierEntries(byTier: Record<string, number>): Array<[string, string, number]> {
  return Object.entries(byTier)
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => tierSortIndex(a) - tierSortIndex(b) || a.localeCompare(b))
    .map(([tier, count]) => [tier, tierLabel(tier), count]);
}

/** Compact chip label: "Expedia — Tier 1, Tier 2, Tier 3" (full detail stays in the tooltip). */
export function formatExclusionRuleShort(line: GHAExclusionRuleLine): string {
  const who = line.supplierName ?? line.supplier ?? 'All suppliers';
  const tiers =
    line.cugTiers != null && line.cugTiers.length > 0 ? line.cugTiers.map(tierLabel).join(', ') : 'all tiers';
  return `${who} — ${tiers}`;
}

/** Share of live properties covered by a discount line, clamped 0–100; null when unknowable. */
export function coveragePercent(propertyCount: number, liveTotal: number): number | null {
  if (liveTotal <= 0) {
    return null;
  }
  return Math.min(100, Math.round((propertyCount / liveTotal) * 100));
}

export function isClassificationTier(value: unknown): value is GHAPropertyClassificationTier {
  return typeof value === 'string' && (CLASSIFICATION_TIER_OPTIONS as Array<string>).includes(value);
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const weekdayLabel = (day: number): string => WEEKDAY_LABELS[day - 1] ?? String(day);

/** "Mon–Fri" / "Sat–Sun" for a consecutive run, "Mon, Wed" otherwise. */
function formatDays(days: Array<number>): string {
  const sorted = [...new Set(days)].sort((a, b) => a - b);
  if (sorted.length === 0) {
    return '';
  }
  const isConsecutive = sorted.every((day, index) => index === 0 || day === sorted[index - 1] + 1);
  if (sorted.length >= 2 && isConsecutive) {
    return `${weekdayLabel(sorted[0])}–${weekdayLabel(sorted[sorted.length - 1])}`;
  }
  return sorted.map(weekdayLabel).join(', ');
}

const pad = (hour: number): string => String(hour).padStart(2, '0');

/**
 * The smart-leak schedule as one line: "Mon–Fri 00–09 · Mon–Fri 17–24 · Sat–Sun all day".
 * Windows are rendered literally rather than inverted into "all day except ..." — an inversion is
 * easy to get subtly wrong, and being wrong about when CUG rates are public is expensive.
 */
export function formatSmartLeakWindows(windows: Array<GHALeakWindow>): string {
  return windows
    .map((window) => {
      const days = formatDays(window.days);
      const hours =
        window.startHour === 0 && window.endHour === 24 ? 'all day' : `${pad(window.startHour)}–${pad(window.endHour)}`;
      return `${days} ${hours}`.trim();
    })
    .join(' · ');
}

/**
 * Tiers that can leak, worth a table row — zero-live tiers are dropped like the chips do. Sorted
 * here rather than trusting the response order, so the rows always match TierChips above.
 */
export function leakingTierLines(byTier: Array<GHACugLeakTierLine>): Array<GHACugLeakTierLine> {
  return byTier
    .filter((line) => line.policy !== 'never' && line.liveTotal > 0)
    .sort((a, b) => tierSortIndex(a.cugTier) - tierSortIndex(b.cugTier));
}

/**
 * How many PROPERTIES on the tier are leaking, and to whom. "international only" means viewers
 * outside the hotel's own country get the CUG rate while domestic viewers get B2C instead.
 * "—" when the account cannot leak at all.
 */
export function formatLeakingNow(line: GHACugLeakTierLine, enabled: boolean): string {
  if (!enabled) {
    return '—';
  }
  if (line.policy === 'always') {
    return `${line.leakingAllViewers.toLocaleString()} to all viewers (24/7)`;
  }
  return (
    `${line.leakingAllViewers.toLocaleString()} to all viewers · ` +
    `${line.leakingInternationalOnly.toLocaleString()} to international only (domestic sees B2C)`
  );
}

/**
 * Footer for the tiers with no row of their own: "Tier 1 · No tier never leak". Whitelisted counts
 * are appended only when non-zero, because a whitelisted-but-ignored property is the one fact about
 * those tiers that appears nowhere else on the page.
 */
export function formatNeverLeakFooter(byTier: Array<GHACugLeakTierLine>): string | null {
  const neverLeak = byTier
    .filter((line) => line.policy === 'never')
    .sort((a, b) => tierSortIndex(a.cugTier) - tierSortIndex(b.cugTier));
  if (neverLeak.length === 0) {
    return null;
  }
  const labels = neverLeak.map((line) => tierLabel(line.cugTier)).join(' · ');
  const ignored = neverLeak.reduce((total, line) => total + line.whitelisted, 0);
  return ignored > 0
    ? `${labels} never leak (${ignored.toLocaleString()} whitelisted — ignored)`
    : `${labels} never leak`;
}

/** Whitelisted smart-tier properties that silently leak nothing because their timezone is unusable. */
export function missingTimezoneCount(byTier: Array<GHACugLeakTierLine>): number {
  return byTier.reduce((total, line) => total + line.missingTimezone, 0);
}
